import React, { useCallback, useMemo } from 'react'
import Alert from '@mui/material/Alert'
import { BasicLocationDTO } from '@sales-center/common'
import { LocationLink } from '../LocationLink'
import { useSelector } from 'react-redux'
import { selectEntityTypeDisplay } from '@user/store/map/slice'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import { getSalesFunnelStage } from '@user/features/MapView/MultiTenantDrawer/MultiTenantDrawerContentSingle/hooks/useData'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import Link from '@mui/material/Link'
import { StatusSection } from './atoms/StatusSection'
import LaunchIcon from '@mui/icons-material/Launch'
import { Theme } from '@mui/material/styles'
import { googleMapsOpenInMapsLink } from '@user/components/HereMap/utils/googleMapsLinks'

type MapBubbleContentsRestrictedProps = {
  location: BasicLocationDTO
  onClose?: () => void
  showCloseBtn?: boolean
}

export default function MapBubbleContentsRestricted({
  location,
  onClose,
  showCloseBtn,
}: MapBubbleContentsRestrictedProps) {
  const address = [
    location.street1,
    location.street2,
    location.city,
    location.stateAbbreviation,
    location.postalCode,
  ]
    .filter(Boolean)
    .join(', ')

  const entityTypeDisplay = useSelector(selectEntityTypeDisplay)

  const status = useMemo(() => {
    return getSalesFunnelStage(entityTypeDisplay, location?.salesFunnelStage)
  }, [entityTypeDisplay, location])

  const knockStatus = useMemo(() => {
    return {
      isDefault:
        location?.knockStatusDetails?.knockStatusSettingsName ===
        'Do not knock',
      status: location?.knockStatusDetails?.knockStatusSettingsName || 'None',
      note: location?.knockStatusDetails?.mostRecentNote || null,
    }
  }, [location?.knockStatusDetails])

  const onAddressMapsClick = useCallback(() => {
    if (!address) {
      return
    }
    const url = googleMapsOpenInMapsLink(address)
    window.open(url, '_blank', 'noopener,noreferrer')
  }, [address])

  const nameAndCompanyDisplay = [
    [location.firstName, location.lastName].filter(Boolean).join(' '),
    location.company,
  ]
    .filter(Boolean)
    .join(' / ')

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="caption">{entityTypeDisplay}</Typography>
        {showCloseBtn && (
          <IconButton
            onClick={onClose}
            size={'small'}
            sx={{
              cursor: 'pointer',
              mr: '-10px',
            }}
            data-testid="action-modal-close-btn"
            data-test-id="mapBubbleRestrictedCloseBtn"
          >
            <CloseIcon fontSize={'small'} />
          </IconButton>
        )}
      </Stack>

      {nameAndCompanyDisplay && (
        <Link
          sx={{
            flexWrap: 'wrap',
            maxWidth: '300px',
          }}
          component="span"
        >
          <Typography variant="h6" sx={{ color: '#1565C0' }}>
            {nameAndCompanyDisplay}
          </Typography>
        </Link>
      )}
      {address ? (
        <Link
          component="button"
          type="button"
          onClick={onAddressMapsClick}
          sx={{
            cursor: 'pointer',
            textAlign: 'left',
            border: 'none',
            background: 'none',
            padding: 0,
            mt: 0.4,
            display: 'inline-flex',
            alignItems: 'center',
            maxWidth: '100%',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: '#1565C0',
              '&:hover': { color: 'rgba(25, 112, 211, 0.9)' },
            }}
          >
            {address}
          </Typography>
          <LaunchIcon
            sx={{
              marginLeft: (theme: Theme) => theme.spacing(0.5),
              fontSize: '18px',
              color: '#1565C0',
              flexShrink: 0,
            }}
          />
        </Link>
      ) : null}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        {location.externalLinkId && location.locationCode && (
          <LocationLink
            externalLocationId={location.externalLinkId}
            externalLocationCode={location.locationCode}
            billingLocation={false}
            typographyVariant="subtitle4"
            fontSize="14px"
            containerSx={{
              alignContent: 'center',
            }}
            linkButtonSx={{
              minWidth: 'fit-content',
              maxWidth: 'fit-content',
              padding: 0.5,
            }}
          />
        )}
      </Stack>
      <StatusSection
        status={status}
        knockStatus={knockStatus}
        chipMarginTop={!location.externalLinkId || !location.locationCode}
      />
      {/* Permission warning */}
      <Alert severity="warning" sx={{ alignItems: 'flex-start', mt: 1.6 }}>
        You don&apos;t have permission to view this record.
      </Alert>
    </>
  )
}
